import { Node, Project, SyntaxKind } from 'ts-morph';

export type PlaywrightAssertionCodeValidation = { valid: true } | { valid: false; reason: string };

export type EvaluateAssertionExpressionValidation = { valid: boolean; reason?: string };

// Mirrors packages/workflow-cache's `containsMacro` helper. browser-core cannot
// import that package directly (workflow-cache depends on browser-core, so the
// reverse import would be circular), so the same `$MACRO(...)` token shape is
// matched locally. `$var.foo` member access is intentionally NOT matched here —
// it is valid JS and parses fine on its own.
const MACRO_TOKEN_PATTERN = /(?<!\\)\$([A-Z_]+)\(/;

const unwrapExpression = (node: Node): Node => {
  let current = node;
  while (
    Node.isParenthesizedExpression(current) ||
    Node.isAsExpression(current) ||
    Node.isTypeAssertion(current) ||
    Node.isNonNullExpression(current) ||
    Node.isSatisfiesExpression(current)
  ) {
    current = current.getExpression();
  }
  return current;
};

/** Detect predicates whose result is structurally independent of page state. */
const isTrivialConstantBoolean = (node: Node): boolean => {
  const current = unwrapExpression(node);
  if (Node.isTrueLiteral(current) || Node.isFalseLiteral(current)) return true;
  if (Node.isPrefixUnaryExpression(current)) {
    return (
      current.getOperatorToken() === SyntaxKind.ExclamationToken &&
      isTrivialConstantBoolean(current.getOperand())
    );
  }
  if (!Node.isCallExpression(current) || current.getArguments().length > 0) return false;

  const callee = unwrapExpression(current.getExpression());
  if (!Node.isArrowFunction(callee) && !Node.isFunctionExpression(callee)) return false;
  if (callee.getParameters().length > 0) return false;
  const body = callee.getBody();
  if (!Node.isBlock(body)) return isTrivialConstantBoolean(body);
  const statements = body.getStatements();
  if (statements.length !== 1 || !Node.isReturnStatement(statements[0])) return false;
  const returned = statements[0].getExpression();
  return returned !== undefined && isTrivialConstantBoolean(returned);
};

/**
 * Recognize an obvious constant predicate in either the canonical expression
 * dialect or the legacy statement-list dialect still present in old rows.
 */
export function isTrivialConstantEvaluateAssertion(condition: string): boolean {
  if (!condition.trim() || MACRO_TOKEN_PATTERN.test(condition)) return false;
  if (!/\b(?:true|false)\b/.test(condition)) return false;
  const project = new Project({ useInMemoryFileSystem: true });
  try {
    const source = project.createSourceFile(
      'evaluate-assertion-expression.ts',
      `const value = (${condition});`
    );
    const initializer = source.getVariableDeclarationOrThrow('value').getInitializerOrThrow();
    if (isTrivialConstantBoolean(initializer)) return true;
  } catch {
    // Fall through to the legacy statement-list shape.
  }

  try {
    const source = project.createSourceFile(
      'evaluate-assertion-statements.ts',
      `function probe() { ${condition} }`
    );
    const body = source.getFunctionOrThrow('probe').getBodyOrThrow();
    if (!Node.isBlock(body)) return false;
    const statements = body.getStatements();
    if (statements.length !== 1 || !Node.isReturnStatement(statements[0])) return false;
    const returned = statements[0].getExpression();
    return returned !== undefined && isTrivialConstantBoolean(returned);
  } catch {
    return false;
  }
}

/**
 * Fail-closed admission check for `assertion.condition` on `type: 'evaluate'`
 * assert actions. Replay executes this string as `page.evaluate(condition)`,
 * which treats a bare string as a JavaScript *expression* — so the contract is
 * that `condition` must itself be syntactically valid as an expression, never
 * human-readable prose.
 */
export function validateEvaluateAssertionExpression(
  condition: string
): EvaluateAssertionExpressionValidation {
  if (!condition.trim()) {
    return { valid: false, reason: 'Evaluate assertion condition is empty.' };
  }

  // Macro substitution happens at replay time, after this string is produced by
  // authoring; raw macro syntax (e.g. `$TODAY()`) may not parse as a JS
  // expression on its own, so skip the syntax check when a macro token is present.
  if (MACRO_TOKEN_PATTERN.test(condition)) {
    return { valid: true };
  }

  try {
    // page.evaluate(str) wraps the string as an expression body, not a
    // statement list, so validate it the same way here.
    new Function(`return (${condition});`);
    if (isTrivialConstantEvaluateAssertion(condition)) {
      return {
        valid: false,
        reason:
          'Evaluate assertion condition is a constant boolean and proves no page state. ' +
          'Read an observable value from the page and compare it with the expected state.',
      };
    }
    return { valid: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      valid: false,
      reason:
        `Evaluate assertion condition has invalid syntax: ${message}. For evaluate ` +
        'assertions, `condition` must be the executable JavaScript expression itself ' +
        '(the human-readable text belongs in `label`).',
    };
  }
}

const MAX_CODE_LENGTH = 8_000;

const FORBIDDEN_METHODS = new Set([
  '$$eval',
  '$eval',
  'addInitScript',
  'addListener',
  'addScriptTag',
  'addStyleTag',
  'assign',
  'back',
  'blur',
  'bringToFront',
  'check',
  'clear',
  'click',
  'close',
  'dblclick',
  'dispatchEvent',
  'defineProperties',
  'defineProperty',
  'dragTo',
  'emit',
  'evaluate',
  'evaluateAll',
  'fill',
  'focus',
  'forward',
  'goto',
  'hover',
  'on',
  'once',
  'pause',
  'pdf',
  'press',
  'pressSequentially',
  'reload',
  'removeListener',
  'request',
  'route',
  'routeFromHAR',
  'screenshot',
  'scrollIntoViewIfNeeded',
  'selectOption',
  'set',
  'setChecked',
  'setContent',
  'setDefaultNavigationTimeout',
  'setDefaultTimeout',
  'setExtraHTTPHeaders',
  'setInputFiles',
  'setViewportSize',
  'tap',
  'type',
  'uncheck',
  'unroute',
  'unrouteAll',
  'waitForEvent',
]);

const POSITIONAL_METHODS = new Set(['first', 'last', 'nth']);
const FORBIDDEN_IDENTIFIERS = new Set([
  'browser',
  'Bun',
  'context',
  'Deno',
  'document',
  'eval',
  'exports',
  'fetch',
  'Function',
  'globalThis',
  'localStorage',
  'module',
  'process',
  'request',
  'require',
  'sessionStorage',
  'window',
]);

let project: Project | undefined;
let sequence = 0;

const getProject = (): Project => {
  project ??= new Project({
    useInMemoryFileSystem: true,
    compilerOptions: { target: 99, module: 99 },
  });
  return project;
};

/**
 * Fail-closed authoring gate for driver-side Playwright assertions.
 *
 * This is a purity/durability boundary, not a general JavaScript sandbox.
 * Assertion code still executes through the same trusted run-code primitive as
 * agent-authored interactions, but it may only observe page state. Unknown
 * local JavaScript is allowed so agents can compare returned strings/counts;
 * page mutation/escape surfaces and positional locators are rejected.
 */
export function validatePlaywrightAssertionCode(code: string): PlaywrightAssertionCodeValidation {
  if (!code.trim()) return { valid: false, reason: 'Playwright assertion code is empty.' };
  if (code.length > MAX_CODE_LENGTH) {
    return {
      valid: false,
      reason: `Playwright assertion code exceeds ${MAX_CODE_LENGTH} characters.`,
    };
  }

  const sourceFile = getProject().createSourceFile(
    `__playwright_assertion_${sequence++}.ts`,
    code,
    { overwrite: true }
  );
  try {
    try {
      // Compile in the same async-body shape used by executeRunCode. This is a
      // syntax check only; evaluating the constructor does not execute code.
      new Function('page', `return (async () => { ${code} })()`);
    } catch (error) {
      return {
        valid: false,
        reason: `Playwright assertion code has invalid syntax: ${
          error instanceof Error ? error.message : String(error)
        }`,
      };
    }

    let hasPageReference = false;
    let hasReturn = false;
    let rejection: string | undefined;
    const rejectMethod = (method: string): boolean => {
      if (FORBIDDEN_METHODS.has(method)) {
        rejection = `Playwright assertions are read-only; .${method}() is not allowed.`;
        return true;
      }
      if (POSITIONAL_METHODS.has(method)) {
        rejection =
          `Playwright assertions cannot use positional .${method}() locators. ` +
          'Narrow by role, label, text, test id, or a semantic parent instead.';
        return true;
      }
      return false;
    };

    sourceFile.forEachDescendant((node, traversal) => {
      if (rejection) {
        traversal.stop();
        return;
      }

      if (Node.isIdentifier(node)) {
        const name = node.getText();
        // The execution wrapper injects `page` without a declaration in this
        // source file. A locally shadowed parameter/binding named page is not
        // browser evidence.
        if (name === 'page' && node.getDefinitions().length === 0) hasPageReference = true;
        if (FORBIDDEN_IDENTIFIERS.has(name) && name !== 'page') {
          rejection = `Playwright assertions cannot access ${name}.`;
          traversal.stop();
        }
        return;
      }

      if (Node.isReturnStatement(node)) {
        hasReturn = true;
        return;
      }

      if (Node.isCallExpression(node)) {
        const expression = node.getExpression();
        if (Node.isPropertyAccessExpression(expression)) {
          if (rejectMethod(expression.getName())) {
            traversal.stop();
          }
        } else if (Node.isElementAccessExpression(expression)) {
          const argument = expression.getArgumentExpression();
          if (!Node.isStringLiteral(argument)) {
            rejection = 'Dynamic method access is not allowed in Playwright assertions.';
            traversal.stop();
          } else if (rejectMethod(argument.getLiteralValue())) {
            traversal.stop();
          }
        }
        return;
      }

      if (
        Node.isBinaryExpression(node) &&
        node.getOperatorToken().getKind() >= SyntaxKind.FirstAssignment &&
        node.getOperatorToken().getKind() <= SyntaxKind.LastAssignment
      ) {
        rejection = 'Assignments are not allowed in read-only Playwright assertions.';
        traversal.stop();
        return;
      }

      if (
        Node.isPrefixUnaryExpression(node) &&
        (node.getOperatorToken() === SyntaxKind.PlusPlusToken ||
          node.getOperatorToken() === SyntaxKind.MinusMinusToken)
      ) {
        rejection = 'Updates are not allowed in read-only Playwright assertions.';
        traversal.stop();
        return;
      }

      if (Node.isPostfixUnaryExpression(node)) {
        rejection = 'Updates are not allowed in read-only Playwright assertions.';
        traversal.stop();
        return;
      }

      if (
        Node.isDeleteExpression(node) ||
        Node.isNewExpression(node) ||
        Node.isImportExpression(node)
      ) {
        rejection = 'Dynamic construction, import, and deletion are not allowed in assertions.';
        traversal.stop();
        return;
      }

      if (
        Node.isElementAccessExpression(node) &&
        node.getExpression().getText().includes('page') &&
        node.getArgumentExpression()?.getKind() !== SyntaxKind.StringLiteral
      ) {
        rejection = 'Dynamic property access on page is not allowed in Playwright assertions.';
        traversal.stop();
      }
    });

    if (rejection) return { valid: false, reason: rejection };
    if (!hasPageReference) {
      return { valid: false, reason: 'Playwright assertion code must read from page.' };
    }
    if (!hasReturn) {
      return {
        valid: false,
        reason: 'Playwright assertion code must return a string, number, boolean, or null.',
      };
    }
    return { valid: true };
  } finally {
    getProject().removeSourceFile(sourceFile);
  }
}
